import { useCallback, useRef, useState } from 'react';
import { FileText, Loader2, Trash2, UploadCloud, X } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { useUploadProviderContract } from '@/features/providers/providers.queries';

import type { ProviderContract, NewProviderPayload, TaxIdType } from '@/features/providers/providers.types';

// ── Types ─────────────────────────────────────────────────────────────────────

interface RegisterProviderModalProps {
  onConfirm: (payload: NewProviderPayload) => void;
  onClose: () => void;
}

// ── Field helper ──────────────────────────────────────────────────────────────

interface FieldProps {
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}

function Field(props: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium">
        {props.label}
        {props.required && <span className="text-error ml-0.5">*</span>}
      </label>
      {props.children}
      {props.error && <p className="text-xs text-error">{props.error}</p>}
    </div>
  );
}

// ── Initial state ─────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  name: '',
  legal_name: '',
  tax_id_type: 'rfc' as TaxIdType,
  tax_id_value: '',
  country_code: 'MEX',
  street: '',
  number: '',
  neighborhood: '',
  city: '',
  state: '',
  postal_code: '',
  address_country: 'México',
  primary_email: '',
  notifications_email: '',
  webhook_url: '',
  client_id: '',
  secret: '',
};

// ── Validation ────────────────────────────────────────────────────────────────

interface FormErrors {
  [key: string]: string;
}

function validate(values: typeof EMPTY_FORM, contracts: ProviderContract[]): FormErrors {
  const errors: FormErrors = {};

  if (values.name.trim().length < 2) errors.name = 'El nombre es requerido';
  if (values.legal_name.trim().length < 2) errors.legal_name = 'La razón social es requerida';
  if (values.tax_id_value.trim().length < 1) errors.tax_id_value = 'El ID fiscal es requerido';
  if (values.country_code.length !== 3) errors.country_code = 'Selecciona un país';
  if (values.street.trim().length < 1) errors.street = 'La calle es requerida';
  if (values.city.trim().length < 1) errors.city = 'La ciudad es requerida';
  if (values.state.trim().length < 1) errors.state = 'El estado es requerido';
  if (values.postal_code.trim().length < 4) errors.postal_code = 'El CP es requerido';
  if (!values.primary_email.includes('@')) errors.primary_email = 'Email inválido';
  if (contracts.length === 0) errors.contracts = 'Se requiere al menos un contrato';

  return errors;
}

// ── Modal ─────────────────────────────────────────────────────────────────────

/**
 * Modal sub-form to register a new provider.
 * Collects fiscal data, integration config and at least one contract file.
 * Contract files are uploaded to GCS via signed URL before the form is confirmed.
 *
 * NOTE: The signed URL endpoint (GET /admin/providers/signed_url) is pending
 * backend implementation. The rest of the form works independently.
 */
export function RegisterProviderModal(props: RegisterProviderModalProps) {
  const [values, setValues] = useState(EMPTY_FORM);
  const [contracts, setContracts] = useState<ProviderContract[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);

  const uploadMutation = useUploadProviderContract();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const setValue = useCallback((field: keyof typeof EMPTY_FORM, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (submitted) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  }, [submitted]);

  const onPickFile = () => {
    fileInputRef.current?.click();
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';

    uploadMutation.mutate(file, {
      onSuccess: (contract) => {
        setContracts((prev) => [...prev, contract]);
        if (submitted) setErrors((prev) => ({ ...prev, contracts: '' }));
      },
    });
  };

  const onRemoveContract = (gcs_path: string) => {
    setContracts((prev) => prev.filter((c) => c.gcs_path !== gcs_path));
  };

  const onConfirm = () => {
    setSubmitted(true);
    const validation_errors = validate(values, contracts);

    if (Object.values(validation_errors).some((e) => e.length > 0)) {
      setErrors(validation_errors);
      return;
    }

    props.onConfirm({
      fiscal: {
        name: values.name,
        legal_name: values.legal_name,
        tax_id_type: values.tax_id_type,
        tax_id_value: values.tax_id_value,
        country_code: values.country_code,
        address: {
          street: values.street,
          number: values.number,
          neighborhood: values.neighborhood,
          city: values.city,
          state: values.state,
          postal_code: values.postal_code,
          country: values.address_country,
        },
        primary_email: values.primary_email,
        notifications_email: values.notifications_email,
      },
      integration: {
        webhook_url: values.webhook_url,
        client_id: values.client_id,
        secret: values.secret,
      },
      contracts,
    });
  };

  return (
    <dialog className="modal modal-open">
      <div className="modal-box w-11/12 max-w-3xl flex flex-col gap-0 p-0 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between border-b border-base-300 px-6 py-4">
          <h3 className="text-lg font-semibold">Dar de alta proveedor</h3>
          <button className="btn btn-ghost btn-sm btn-square" onClick={props.onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-6 overflow-y-auto px-6 py-5">

          {/* ── Datos fiscales ── */}
          <section className="flex flex-col gap-4">
            <h4 className="text-sm font-semibold text-base-content/60 uppercase tracking-wide">
              Datos fiscales
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Nombre comercial" error={errors.name} required>
                <input
                  className={twMerge('input input-bordered w-full', errors.name && 'input-error')}
                  value={values.name}
                  onChange={(e) => setValue('name', e.target.value)}
                  placeholder="Ej. MBE México"
                />
              </Field>

              <Field label="Razón social" error={errors.legal_name} required>
                <input
                  className={twMerge('input input-bordered w-full', errors.legal_name && 'input-error')}
                  value={values.legal_name}
                  onChange={(e) => setValue('legal_name', e.target.value)}
                  placeholder="Ej. MBE México S.A. de C.V."
                />
              </Field>

              <Field label="País" error={errors.country_code} required>
                <select
                  className="select select-bordered w-full"
                  value={values.country_code}
                  onChange={(e) => setValue('country_code', e.target.value)}
                >
                  <option value="MEX">México</option>
                  <option value="COL">Colombia</option>
                  <option value="CHL">Chile</option>
                  <option value="PER">Perú</option>
                </select>
              </Field>

              <Field label="Tipo de ID fiscal" required>
                <select
                  className="select select-bordered w-full"
                  value={values.tax_id_type}
                  onChange={(e) => setValue('tax_id_type', e.target.value)}
                >
                  <option value="rfc">RFC (México)</option>
                  <option value="nit">NIT (Colombia)</option>
                  <option value="rut">RUT (Chile)</option>
                  <option value="ruc">RUC (Perú)</option>
                </select>
              </Field>

              <Field label="RFC / ID fiscal" error={errors.tax_id_value} required>
                <input
                  className={twMerge('input input-bordered w-full', errors.tax_id_value && 'input-error')}
                  value={values.tax_id_value}
                  onChange={(e) => setValue('tax_id_value', e.target.value)}
                  placeholder="XAXX010101000"
                />
              </Field>

              <Field label="Correo principal" error={errors.primary_email} required>
                <input
                  type="email"
                  className={twMerge('input input-bordered w-full', errors.primary_email && 'input-error')}
                  value={values.primary_email}
                  onChange={(e) => setValue('primary_email', e.target.value)}
                  placeholder="contacto@proveedor.com"
                />
              </Field>

              <Field label="Correo de notificaciones" error={errors.notifications_email}>
                <input
                  type="email"
                  className="input input-bordered w-full"
                  value={values.notifications_email}
                  onChange={(e) => setValue('notifications_email', e.target.value)}
                  placeholder="notificaciones@proveedor.com (opcional)"
                />
              </Field>
            </div>

            {/* Dirección fiscal */}
            <h5 className="text-xs font-medium text-base-content/50 uppercase tracking-wide mt-1">
              Dirección fiscal
            </h5>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Calle" error={errors.street} required>
                <input
                  className={twMerge('input input-bordered w-full', errors.street && 'input-error')}
                  value={values.street}
                  onChange={(e) => setValue('street', e.target.value)}
                />
              </Field>
              <Field label="Número">
                <input
                  className="input input-bordered w-full"
                  value={values.number}
                  onChange={(e) => setValue('number', e.target.value)}
                />
              </Field>
              <Field label="Colonia">
                <input
                  className="input input-bordered w-full"
                  value={values.neighborhood}
                  onChange={(e) => setValue('neighborhood', e.target.value)}
                />
              </Field>
              <Field label="Ciudad" error={errors.city} required>
                <input
                  className={twMerge('input input-bordered w-full', errors.city && 'input-error')}
                  value={values.city}
                  onChange={(e) => setValue('city', e.target.value)}
                />
              </Field>
              <Field label="Estado" error={errors.state} required>
                <input
                  className={twMerge('input input-bordered w-full', errors.state && 'input-error')}
                  value={values.state}
                  onChange={(e) => setValue('state', e.target.value)}
                />
              </Field>
              <Field label="Código postal" error={errors.postal_code} required>
                <input
                  className={twMerge('input input-bordered w-full', errors.postal_code && 'input-error')}
                  value={values.postal_code}
                  onChange={(e) => setValue('postal_code', e.target.value)}
                />
              </Field>
              <Field label="País (dirección)">
                <input
                  className="input input-bordered w-full"
                  value={values.address_country}
                  onChange={(e) => setValue('address_country', e.target.value)}
                />
              </Field>
            </div>
          </section>

          {/* ── Integración ── */}
          <section className="flex flex-col gap-4">
            <h4 className="text-sm font-semibold text-base-content/60 uppercase tracking-wide">
              Integración
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Webhook URL">
                <input
                  className="input input-bordered w-full"
                  value={values.webhook_url}
                  onChange={(e) => setValue('webhook_url', e.target.value)}
                  placeholder="https://proveedor.com/webhook"
                />
              </Field>
              <Field label="Client ID">
                <input
                  className="input input-bordered w-full"
                  value={values.client_id}
                  onChange={(e) => setValue('client_id', e.target.value)}
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Secret">
                  <input
                    type="password"
                    className="input input-bordered w-full"
                    value={values.secret}
                    onChange={(e) => setValue('secret', e.target.value)}
                  />
                </Field>
              </div>
            </div>
          </section>

          {/* ── Contratos ── */}
          <section className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-base-content/60 uppercase tracking-wide">
                Contratos <span className="text-error">*</span>
              </h4>
              <button
                type="button"
                className="btn btn-outline btn-sm gap-2"
                onClick={onPickFile}
                disabled={uploadMutation.isPending}
              >
                {uploadMutation.isPending
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <UploadCloud className="h-4 w-4" />
                }
                Subir contrato
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx"
                onChange={onFileChange}
              />
            </div>

            {errors.contracts && (
              <p className="text-xs text-error">{errors.contracts}</p>
            )}

            {contracts.length === 0 ? (
              <div className="rounded-box border border-dashed border-base-300 py-8 text-center text-sm text-base-content/40">
                Sin contratos subidos todavía. Se requiere al menos uno.
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {contracts.map((contract) => (
                  <li
                    key={contract.gcs_path}
                    className="flex items-center gap-3 rounded-box border border-base-300 bg-base-100 px-4 py-2"
                  >
                    <FileText className="h-4 w-4 shrink-0 text-primary" />
                    <span className="flex-1 truncate text-sm">{contract.file_name}</span>
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs btn-square text-error"
                      onClick={() => onRemoveContract(contract.gcs_path)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-base-300 px-6 py-4">
          <button className="btn btn-ghost" onClick={props.onClose}>
            Cancelar
          </button>
          <button
            className="btn btn-success text-white"
            onClick={onConfirm}
            disabled={uploadMutation.isPending}
          >
            Guardar proveedor
          </button>
        </div>
      </div>

      {/* Backdrop */}
      <div className="modal-backdrop" onClick={props.onClose} />
    </dialog>
  );
}
